import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Briefcase, Wallet, Building2, Users, Megaphone, LifeBuoy, ClipboardList, RefreshCw, Send } from "lucide-react";

const DEPT_THEME = {
  finance: { icon: <Wallet size={16}/>, color: "lime", title: "Finance Desk" },
  operations: { icon: <Building2 size={16}/>, color: "pink", title: "Operations Hub" },
  sales: { icon: <Users size={16}/>, color: "blue", title: "Sales Cockpit" },
  marketing: { icon: <Megaphone size={16}/>, color: "lime", title: "Marketing Studio" },
  support: { icon: <LifeBuoy size={16}/>, color: "pink", title: "Support Inbox" },
  ground_staff: { icon: <ClipboardList size={16}/>, color: "white", title: "Ground Staff Tasks" },
};

export default function StaffPanel() {
  const { user, loading } = useAuth();
  const [meta, setMeta] = useState(null);
  const [data, setData] = useState({});

  const reload = async () => {
    if (!user) return;
    try {
      const r = await api.get("/staff/me");
      setMeta(r.data);
      const dept = r.data.department;
      const calls = {};
      if (dept === "finance" || dept === "admin") {
        calls.bookings = api.get("/bookings").then(x => x.data);
        calls.rent = api.get("/rent/schedule").then(x => x.data);
        calls.refunds = api.get("/refunds").then(x => x.data);
      }
      if (dept === "operations" || dept === "admin") {
        calls.properties = api.get("/properties", { params: { status: "pending_approval" } }).then(x => x.data);
        calls.users = api.get("/admin/users").then(x => x.data).catch(() => []);
        calls.checkins = api.get("/checkins/template").then(x => x.data.items);
      }
      if (dept === "sales" || dept === "admin") {
        calls.enquiries = api.get("/enquiries").then(x => x.data);
        calls.bookingsAll = api.get("/bookings").then(x => x.data);
      }
      if (dept === "support" || dept === "admin") {
        calls.complaints = api.get("/complaints").then(x => x.data);
        calls.roomService = api.get("/room-service").then(x => x.data);
      }
      if (dept === "marketing" || dept === "admin") {
        calls.blog = api.get("/blog").then(x => x.data);
        calls.contact = api.get("/contact/messages").then(x => x.data).catch(() => []);
      }
      if (dept === "ground_staff") {
        calls.roomService = api.get("/room-service").then(x => x.data);
      }
      const resolved = {};
      await Promise.all(Object.entries(calls).map(async ([k, p]) => { try { resolved[k] = await p; } catch { resolved[k] = []; } }));
      setData(resolved);
    } catch (e) {
      console.warn(e);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-5 py-20">Loading…</div>;
  if (!user || !["staff", "admin", "super_admin"].includes(user.role)) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center" data-testid="staff-no-access">
        <h1 className="font-display font-black text-4xl">Staff only</h1>
        <p className="text-[#484848] mt-2">Sign in with a staff account to see your departmental cockpit.</p>
        <Link to="/login" className="brut-btn mt-6 inline-flex">Sign in</Link>
      </div>
    );
  }
  const dept = meta?.department || "admin";
  const theme = DEPT_THEME[dept] || { icon: <Briefcase size={16}/>, color: "lime", title: "Staff" };

  return (
    <div className="panel-shell">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-10" data-testid="staff-panel">
      <div className="brut-card p-6 bg-[#0F1F17] text-[#FDFCF8]">
        <div className="flex items-center gap-2"><span className="brut-tag lime flex items-center gap-1">{theme.icon} {dept.toUpperCase()}</span>{user.sub_role && <span className="brut-tag pink">{user.sub_role}</span>}</div>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-2">{theme.title}</h1>
        <p className="text-[#bdbdbd] mt-2 text-sm">Welcome {user.name?.split(" ")[0]} — your scoped work queue.</p>
      </div>

      {meta?.counts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6" data-testid="staff-counts">
          {Object.entries(meta.counts).map(([k, v]) => (
            <div key={k} className="brut-card p-4">
              <div className="text-xs uppercase tracking-wider text-[#484848]">{k.replace(/_/g, " ")}</div>
              <div className="font-display font-black text-3xl">{v}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 space-y-6">
        {(dept === "finance" || dept === "admin") && <FinanceSection data={data} refresh={reload}/>}
        {(dept === "operations" || dept === "admin") && <OperationsSection data={data} refresh={reload}/>}
        {(dept === "sales" || dept === "admin") && <SalesSection data={data} refresh={reload}/>}
        {(dept === "support" || dept === "admin") && <SupportSection data={data} refresh={reload}/>}
        {(dept === "marketing" || dept === "admin") && <MarketingSection data={data}/>}
        {dept === "ground_staff" && <GroundSection data={data} refresh={reload}/>}
      </div>
    </div>
    </div>
  );
}

function FinanceSection({ data, refresh }) {
  const rent = data.rent || [];
  const overdue = rent.filter(r => r.status !== "paid");
  const triggerScan = async () => {
    try {
      const { data: res } = await api.post("/rent/scan-reminders");
      toast.success(`Reminders processed: ${res.sent?.length || 0}`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
  };
  return (
    <section data-testid="finance-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-black text-2xl">Rent schedules</h2>
        <button className="brut-btn pink" onClick={triggerScan} data-testid="rent-scan-btn"><Send size={14}/> Run reminder scan</button>
      </div>
      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]">
            <tr><th className="p-3 text-left">Property</th><th className="p-3 text-left">Month</th><th className="p-3 text-left">Due</th><th className="p-3 text-left">Amount</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Reminders</th></tr>
          </thead>
          <tbody>
            {rent.map(r => (
              <tr key={r.id} className="border-b border-[#0F1F17]/20" data-testid={`rent-${r.id}`}>
                <td className="p-3">{r.property_title}</td>
                <td className="p-3">#{r.month_index}</td>
                <td className="p-3">{r.due_date}</td>
                <td className="p-3">₹{r.amount?.toLocaleString()}</td>
                <td className="p-3"><span className={`brut-tag ${r.status === "paid" ? "lime" : "pink"} !text-[10px]`}>{r.status}</span></td>
                <td className="p-3 text-xs">{(r.reminders_sent || []).join(", ") || "—"}</td>
              </tr>
            ))}
            {rent.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#484848]">No rent schedules yet. Once tenants pay first month, schedules auto-generate.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#484848] mt-2">Pending {overdue.length} of {rent.length}. Reminder scan sends WhatsApp at T-3, due date, and T+3.</p>
    </section>
  );
}

function OperationsSection({ data, refresh }) {
  const pending = data.properties || [];
  const approve = async (pid, status) => {
    try { await api.patch(`/properties/${pid}/status`, null, { params: { status } }); toast.success(`Property ${status}`); refresh(); }
    catch { toast.error("Failed"); }
  };
  return (
    <section data-testid="operations-section">
      <h2 className="font-display font-black text-2xl mb-3">Properties awaiting approval</h2>
      <div className="grid md:grid-cols-2 gap-4">
        {pending.map(p => (
          <div key={p.id} className="brut-card p-4" data-testid={`pending-prop-${p.id}`}>
            <div className="font-bold">{p.title}</div>
            <div className="text-sm text-[#484848]">{p.city} · ₹{p.price_per_month}/mo · {p.gender}</div>
            <div className="flex gap-2 mt-3">
              <button className="brut-btn !py-1 !px-3 !text-xs" onClick={()=>approve(p.id, "active")}>Approve</button>
              <button className="brut-btn pink !py-1 !px-3 !text-xs" onClick={()=>approve(p.id, "rejected")}>Reject</button>
            </div>
          </div>
        ))}
        {pending.length === 0 && <div className="brut-card p-6 text-center text-[#484848] md:col-span-2">No properties pending approval.</div>}
      </div>
    </section>
  );
}

function SalesSection({ data }) {
  const enquiries = data.enquiries || [];
  return (
    <section data-testid="sales-section">
      <h2 className="font-display font-black text-2xl mb-3">Leads · {enquiries.length}</h2>
      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]"><tr><th className="p-3 text-left">Student</th><th className="p-3 text-left">Property</th><th className="p-3 text-left">Move-in</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Date</th></tr></thead>
          <tbody>
            {enquiries.map(e => (
              <tr key={e.id} className="border-b border-[#0F1F17]/20">
                <td className="p-3">{e.student_name}<div className="text-xs text-[#484848]">{e.student_email}</div></td>
                <td className="p-3">{e.property_title}</td>
                <td className="p-3 text-xs">{e.move_in_date || "—"}</td>
                <td className="p-3"><span className="brut-tag white !text-[10px]">{e.status}</span></td>
                <td className="p-3 text-xs">{e.created_at?.slice(0, 10)}</td>
              </tr>
            ))}
            {enquiries.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#484848]">No leads yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SupportSection({ data, refresh }) {
  const complaints = data.complaints || [];
  const rs = data.roomService || [];
  const mark = async (cid, status) => {
    try { await api.patch(`/complaints/${cid}/status`, null, { params: { status } }); toast.success("Updated"); refresh(); } catch { toast.error("Failed"); }
  };
  const rsMark = async (rid, status) => {
    try { await api.patch(`/room-service/${rid}/status`, null, { params: { status } }); toast.success("Updated"); refresh(); } catch { toast.error("Failed"); }
  };
  return (
    <section data-testid="support-section" className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl mb-3">Open complaints</h2>
        <div className="space-y-2">
          {complaints.filter(c => c.status === "open").map(c => (
            <div key={c.id} className="brut-card p-4">
              <div className="flex flex-wrap justify-between gap-2 items-center">
                <div className="font-bold">{c.subject}</div>
                <span className={`brut-tag ${c.priority === "high" ? "pink" : "white"} !text-[10px]`}>{c.priority}</span>
              </div>
              <p className="text-sm text-[#484848] mt-1">{c.description}</p>
              <div className="flex gap-2 mt-3">
                <button className="brut-btn !py-1 !px-3 !text-xs" onClick={()=>mark(c.id, "resolved")}>Mark resolved</button>
                <button className="brut-btn pink !py-1 !px-3 !text-xs" onClick={()=>mark(c.id, "in_progress")}>In progress</button>
              </div>
            </div>
          ))}
          {complaints.filter(c => c.status === "open").length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No open complaints.</div>}
        </div>
      </div>
      <div>
        <h2 className="font-display font-black text-2xl mb-3">Room service requests</h2>
        <div className="space-y-2">
          {rs.map(r => (
            <div key={r.id} className="brut-card p-4 flex flex-wrap justify-between gap-2 items-center">
              <div>
                <div className="font-bold capitalize">{r.category}</div>
                <p className="text-sm text-[#484848]">{r.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="brut-tag white !text-[10px]">{r.status}</span>
                <select className="brut-input !py-1 !text-xs" defaultValue={r.status} onChange={(e)=>rsMark(r.id, e.target.value)}>
                  {["open", "assigned", "in_progress", "done", "cancelled"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
          ))}
          {rs.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No room-service requests.</div>}
        </div>
      </div>
    </section>
  );
}

function MarketingSection({ data }) {
  const blog = data.blog || [];
  const contact = data.contact || [];
  return (
    <section data-testid="marketing-section" className="grid lg:grid-cols-2 gap-6">
      <div>
        <h2 className="font-display font-black text-2xl mb-3">Blog posts · {blog.length}</h2>
        <div className="space-y-2">
          {blog.map(b => (
            <div key={b.id} className="brut-card p-3 text-sm">
              <div className="font-bold">{b.title}</div>
              <div className="text-xs text-[#484848]">{b.category}</div>
            </div>
          ))}
        </div>
      </div>
      <div>
        <h2 className="font-display font-black text-2xl mb-3">Contact messages · {contact.length}</h2>
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {contact.map(c => (
            <div key={c.id} className="brut-card p-3 text-sm">
              <div className="flex justify-between">
                <strong>{c.name}</strong><span className="text-xs text-[#484848]">{c.created_at?.slice(0, 10)}</span>
              </div>
              <div className="text-xs text-[#484848]">{c.email}</div>
              <div className="font-medium mt-1">{c.subject}</div>
              <p className="text-xs mt-1">{c.message}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function GroundSection({ data, refresh }) {
  const rs = (data.roomService || []).filter(r => r.status !== "done" && r.status !== "cancelled");
  const update = async (rid, status) => {
    try { await api.patch(`/room-service/${rid}/status`, null, { params: { status } }); toast.success("Updated"); refresh(); } catch { toast.error("Failed"); }
  };
  return (
    <section data-testid="ground-section">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display font-black text-2xl">My active tasks</h2>
        <button className="brut-tag white cursor-pointer flex items-center gap-1" onClick={refresh}><RefreshCw size={12}/> Refresh</button>
      </div>
      <div className="space-y-2">
        {rs.map(r => (
          <div key={r.id} className="brut-card p-4 flex flex-wrap justify-between gap-2 items-center" data-testid={`ground-task-${r.id}`}>
            <div>
              <div className="font-bold capitalize">{r.category}</div>
              <p className="text-sm text-[#484848]">{r.description}</p>
              {r.scheduled_for && <div className="text-xs">Scheduled: {r.scheduled_for}</div>}
            </div>
            <div className="flex gap-2 items-center">
              <span className="brut-tag white !text-[10px]">{r.status}</span>
              <button className="brut-btn !py-1 !px-2 !text-xs" onClick={()=>update(r.id, "in_progress")}>Start</button>
              <button className="brut-btn pink !py-1 !px-2 !text-xs" onClick={()=>update(r.id, "done")}>Done</button>
            </div>
          </div>
        ))}
        {rs.length === 0 && <div className="brut-card p-6 text-center text-[#484848]">No tasks assigned right now.</div>}
      </div>
    </section>
  );
}
